// ==========================================================================
// screens/Login.tsx — email + password sign in (FF-003, 9 Oct 2026)
// No sign-up form: logins are created in the Supabase dashboard by James,
// and the household membership is seeded for that email (migration 002).
// 10 Oct (FF-061): remembers the last email on this device, and the fields
// carry the names password managers look for, so the phone can fill both.
// ==========================================================================
import { useState, type FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import { Brand } from "@/components/Brand";
import { Button, Card, ErrorText, Field } from "@/components/ui";
import { lastEmail, rememberEmail } from "@/lib/quickSignIn";
import { signedOutWhy } from "@/lib/auth";

export default function Login() {
  const [email, setEmail] = useState(lastEmail);
  const remembered = !!lastEmail();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
    setBusy(false);
    if (error) setError("That email and password don't match.");
    else rememberEmail(email.trim().toLowerCase());
    // On success the AuthProvider notices the session and moves to MFA.
  }

  return (
    <main className="mx-auto max-w-md px-4 py-10 space-y-6">
      <Brand />
      <Card>
        <form onSubmit={submit} method="post" action="#" className="space-y-4">
          {signedOutWhy() && <p className="rounded-md bg-surface-raised px-3 py-2 text-sm text-ink-muted">{signedOutWhy()}</p>}
          {remembered && email ? (
            <div className="flex items-center justify-between gap-3 rounded-md bg-surface-raised px-3 py-3">
              <div className="min-w-0"><div className="text-xs text-ink-muted">Signing in as</div><div className="truncate font-medium">{email}</div></div>
              <button type="button" onClick={() => { setEmail(""); rememberEmail(""); }} className="shrink-0 text-sm text-primary">Not you?</button>
              <input type="hidden" name="email" autoComplete="username" value={email} readOnly />
            </div>
          ) : (
            <Field label="Email" id="email" name="email" type="email" autoComplete="username" inputMode="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          )}
          <Field label="Password" id="password" name="password" type="password" autoComplete="current-password" required autoFocus={remembered} value={password} onChange={(e) => setPassword(e.target.value)} />
          <ErrorText>{error}</ErrorText>
          <Button type="submit" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</Button>
        </form>
      </Card>
    </main>
  );
}
